import { useTranslations } from "@zapier/i18n";
import { useEffect, useState } from "react";
import {
  BetaDataTableCell as Cell,
  VisuallyHidden,
} from "@zapier/design-system";
import {
  FolderContext,
  FolderTreeItem,
  useFolderActionPermissions,
} from "@zapier/client";
import { OwnerCell, Row, TitleCell, FolderModal } from "@zapier/shared";
import { AssetTypes, getFolderUrl } from "@zapier/utils";

import { useFolderActionItems } from "../useFolderActionItems";
import styles from "./FolderRow.module.css";

interface Props {
  folder: FolderTreeItem;
  folderLevel: number;
  folderZapTableRow?: boolean;
  isFolderExpanded?: boolean;
  isFoldersExpandedByDefault?: boolean;
  onExpand?: (isFolderExpanded: boolean) => void;
  searchQuery?: string | null;
  showLastRun?: boolean;
  includeSelectionColumn?: boolean; // Whether to include a column for selection checkboxes
  setCurrentModal: (modal: FolderModal | undefined) => void;
  setCurrentFolder: (folder: FolderTreeItem | undefined) => void;
  isChildRow?: boolean;
}

const FolderRowBranch = ({
  folder,
  folderLevel,
  folderZapTableRow,
  isFolderExpanded,
  onExpand,
  searchQuery,
  showLastRun,
  includeSelectionColumn,
  setCurrentModal,
  setCurrentFolder,
  isChildRow,
}: Props) => {
  const t = useTranslations("folder-row");

  const { enablePermissionsCheck } = useFolderActionPermissions({
    folderId: folder.id,
    folderDepth: folderLevel,
  });

  const folderActionItems = useFolderActionItems({
    folder,
    folderLevel,
    setCurrentFolder,
    setCurrentModal,
  });

  return (
    <Row
      allowsSelection={false}
      hiddenSelection={includeSelectionColumn}
      id={String(folder.id)}
      assetName={folder.name}
      kebabMenuLabel={t("labels.folder-options")}
      kebabMenuAriaLabel={t("labels.folder-options-for", { name: folder.name })}
      menuItems={folderActionItems}
      onOpenMenu={enablePermissionsCheck}
      assetType={AssetTypes.Folder}
      className={`${isChildRow ? styles.childRow : ""} ${folderZapTableRow ? styles.folderZapTableRow : ""}`}
    >
      <TitleCell
        color="GrayWarm6"
        icon={folder.isShared ? "team" : "navFolder"}
        title={folder.name}
        searchQuery={searchQuery}
        url={getFolderUrl(folder.id)}
        id={folder.id}
        accordionLevel={folderLevel}
        hasAccordion={!!folder.children?.length}
        allowAccordion={true}
        isAccordionExpanded={isFolderExpanded}
        onExpandAccordion={onExpand}
        hiddenSelection={includeSelectionColumn}
        assetType={AssetTypes.Folder}
      />
      {folderZapTableRow && (
        <>
          <Cell>
            <VisuallyHidden>{t("columns.status")}</VisuallyHidden>
          </Cell>
          <Cell>
            <VisuallyHidden>{t("columns.apps")}</VisuallyHidden>
          </Cell>
          <Cell>
            <VisuallyHidden>{t("columns.last-modified")}</VisuallyHidden>
          </Cell>
          {showLastRun && (
            <Cell>
              <VisuallyHidden>{t("columns.last-run")}</VisuallyHidden>
            </Cell>
          )}
        </>
      )}
      <OwnerCell
        name={folder.owner?.fullName}
        photoUrl={folder.owner?.photoUrl}
      />
    </Row>
  );
};

const FolderRow = ({
  folder,
  folderLevel = 0,
  folderZapTableRow,
  searchQuery,
  showLastRun,
  isFoldersExpandedByDefault,
  includeSelectionColumn,
  setCurrentFolder,
  setCurrentModal,
  isChildRow,
}: Props) => {
  const [isFolderExpanded, setIsFolderExpanded] = useState(false);

  useEffect(() => {
    // Expand folders by default if search query is present
    if (isFoldersExpandedByDefault) {
      setIsFolderExpanded(true);
    } else {
      // Close folders if search query is removed
      setIsFolderExpanded(false);
    }
  }, [folder, isFoldersExpandedByDefault]);

  return (
    <FolderContext.Provider value={folder}>
      <FolderRowBranch
        folder={folder}
        folderLevel={folderLevel}
        folderZapTableRow={folderZapTableRow}
        isFolderExpanded={isFolderExpanded}
        onExpand={setIsFolderExpanded}
        searchQuery={searchQuery}
        showLastRun={showLastRun}
        includeSelectionColumn={includeSelectionColumn}
        setCurrentFolder={setCurrentFolder}
        setCurrentModal={setCurrentModal}
        isChildRow={isChildRow}
      />
      {/* Recursively render children in the same row */}
      {isFolderExpanded &&
        folder?.children?.map((child) => (
          <FolderRow
            folder={child as FolderTreeItem}
            folderLevel={folderLevel + 1}
            folderZapTableRow={folderZapTableRow}
            isFoldersExpandedByDefault={isFoldersExpandedByDefault}
            key={child.id}
            searchQuery={searchQuery}
            showLastRun={showLastRun}
            includeSelectionColumn={includeSelectionColumn}
            setCurrentFolder={setCurrentFolder}
            setCurrentModal={setCurrentModal}
            isChildRow={true}
          />
        ))}
    </FolderContext.Provider>
  );
};

export default FolderRow;
